import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AbcCore } from '@barthauer/abc/core';
import { ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { UserService } from '@barthauer/aptis-authentication';
import dxForm from 'devextreme/ui/form';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserViewModel } from './models/view.model';
import { ContextService } from './services/context.service';
import { DataService, PersistData } from './services/data.service';
import { WebApiService } from './services/web-api.service';

@Component({
  selector: 'barthauer-abc-management-users-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent implements OnInit {
  @Output() readonly changedEvent = new EventEmitter<void>();
  @Output() onPasswordChanged: EventEmitter<void> = new EventEmitter();
  @Input() allowUserNameChange: boolean = true;

  private saveData$ = new Subject<PersistData>();
  private refreshData$ = new Subject<void>();
  readonly persistData$: Observable<void>;
  readonly queryData$: Observable<void>;
  errorMessage = '';
  isSaving = false;
  isLoading = false;
  isPasswordChanging = false;
  passwordRequirementsArray: string[] = [];

  user?: UserViewModel;

  get isPending(): boolean {
    return this.isLoading || this.isSaving || this.isPasswordChanging;
  }

  get isUserChanged(): boolean {
    return this.user !== undefined && this.user.hasChanges;
  }

  get isPasswordChanged(): boolean {
    return this.user !== undefined && this.user.password.hasChanges;
  }

  get isUserValid(): boolean {
    return this.user !== undefined && this.user.isValid;
  }

  get isPasswordValid(): boolean {
    return this.user !== undefined && this.user.password.isValid;
  }

  get res(): ResourceSet {
    return this.contextService.res;
  }

  readonly currentPasswordTextBoxButton = this.createDxTextBoxButton('password.currentPassword');
  readonly newPasswordTextBoxButton = this.createDxTextBoxButton('password.newPassword');
  readonly confirmPasswordTextBoxButton = this.createDxTextBoxButton('password.confirmPassword');

  constructor(
    readonly contextService: ContextService,
    private dataService: DataService,
    private router: Router,
    private coreService: AbcCore,
    private messageBoxService: MessageBoxService,
    private webApiService: WebApiService,
    private userService: UserService
  ) {
    this.queryData$ = this.dataService.queryUser(this, this.refreshData$);
    this.persistData$ = this.dataService.persistData(this, this.saveData$, this.refreshData$);
  }

  ngOnInit() {
    this.readPasswordRequirements();
  }

  readonly saveUser = () => this.saveUserCore(() => this.changedEvent.emit());
  private saveUserCore(continueWith?: () => void, noRefresh?: boolean): void {
    const persistData = new PersistData(() => this.dataService.persistUser(this.user!));
    persistData.continueWith = continueWith;
    persistData.noRefresh = noRefresh;
    this.saveData$.next(persistData);
  }

  readonly changePassword = () =>
    this.changePasswordCore(
      this.user!.password.currentPassword,
      this.user!.password.newPassword,
      this.user!.password.confirmPassword
    );
  private changePasswordCore(oldPassword: string, newPassword: string, confirmPassword: string): void {
    this.isPasswordChanging = true;
    this.webApiService
      .queryChangePassword(oldPassword, newPassword, confirmPassword)
      .pipe(take(1))
      .subscribe(
        (success) => {
          alert(this.res.get('UserSettings-PasswordChanged'));
          this.changedEvent.emit();
          this.refreshData$.next();
          this.onPasswordChanged.emit();
        },
        (error) => {
          this.coreService.error.publish(error);
        }
      )
      .add(() => {
        this.isPasswordChanging = false;
      });
  }

  private navigate(url: string): void {
    if (this.isUserChanged) {
      const settings = new MessageSettings();
      settings.caption = this.res.get('UserSettings-UserChanged');

      if (this.isUserValid) {
        settings.mode = MessageMode.Question;
        settings.button = MessageBoxButtons.YesNoCancel;
        settings.message = this.res.get('UserSettings-SaveChanges');
        this.showMessageBox(settings, (dialogResult) => {
          if (dialogResult === DialogResult.Yes) {
            this.saveUserCore(() => this.router.navigate([url]), true);
          } else if (dialogResult === DialogResult.No) {
            this.router.navigate([url]);
          }
        });
      } else {
        settings.mode = MessageMode.Info;
        settings.button = MessageBoxButtons.OKCancel;
        settings.message = this.res.get('UserSettings-DiscardChanges');
        this.showMessageBox(settings, (dialogResult) => {
          if (dialogResult === DialogResult.OK) {
            this.router.navigate([url]);
          }
        });
      }
    } else {
      this.router.navigate([url]);
    }
  }

  private showMessageBox(settings: MessageSettings, handleResult: (dialogResult: DialogResult) => void): void {
    this.messageBoxService
      .showMessageBox(settings)
      .pipe(take(1))
      .subscribe((dialogResult) => handleResult(dialogResult));
  }

  private createDxTextBoxButton(dataField: string): [any] {
    return [
      {
        name: dataField,
        options: {
          icon: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAkCAYAAADhAJiYAAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB7klEQVRYw+2YP0tcQRTFz65xFVJZpBBS2O2qVSrRUkwqYfUDpBbWQu3ELt/HLRQ/Q8RCGxVJrRDEwj9sTATxZ/Hugo4zL/NmV1xhD9xi59177pl9986fVwLUSyi/tYC+oL6gbuNDYtyUpLqkaUmfJY3a+G9JZ5J2JW1J2ivMDBSxeWCfeBxYTHSOWMcRYLOAEBebxtEVQWPASQdi2jgxro4E1YDTQIJjYM18hszGbew4EHNq/kmCvgDnHtI7YBko58SWgSXg1hN/btyFBM0AlwExczG1YDZrMS4uLUeUoDmgFfjLGwXEtG05wNXyTc4NXgzMCOAIGHD8q0ATuDZrempkwGJ9+AfUQ4K+A/eEseqZ/UbgdUw4fqs5vPeW+5mgBvBAPkLd8cPju+341P7D/WAaJGCdOFQI14kr6o/zvBKZYz11L5Okv5KGA89Kzu9K0b0s5ZXt5PjuOL6TRV5ZalFP4F+rrnhZ1Cs5vN6ijmn7Q162/ThZq9+YNW3MbfvDAOed5cxdGL+RFaUPKQtjI8DVAr66/u9i6+jJzTXm+HFEVqxVYBD4SNZNKzk109HxoycPaG0bIeugVDTp4hH2qdXJDu6xOAAWiuQoQdLHhvY1aEZSVdInG7+Q9EvSz9RrUKqgV0PP3Vz7gvqCOsUj+CxC9LB1Dc8AAAASdEVYdEVYSUY6T3JpZW50YXRpb24AMYRY7O8AAAAASUVORK5CYII=',
          type: 'default',
          tabIndex: '-1',
          onClick: (e: any) => {
            const editor = (<dxForm>dxForm.getInstance(<Element>document.getElementById('dxForm'))).getEditor(
              dataField
            )!;
            if (editor.option('mode') === 'password') {
              editor.option('mode', 'text');
            } else {
              editor.option('mode', 'password');
            }
          },
        },
      },
    ];
  }

  private async readPasswordRequirements() {
    const loginInfo = await this.userService.getCurrentLoginInfo().toPromise();
    const requirements = loginInfo.passwordRequirements;
    setTimeout(() => {
      if (requirements) {
        if (requirements.minLength > 0 && requirements.maxLength < Infinity) {
          this.passwordRequirementsArray.push(
            requirements.minLength + '-' + requirements.maxLength + this.res.get('UserSettings-PasswordLength')
          );
        } else if (requirements.minLength) {
          this.passwordRequirementsArray.push(this.res.get('Usersettings-PasswordMinLength') + requirements.minLength);
        } else if (requirements.maxLength < Infinity) {
          this.passwordRequirementsArray.push(this.res.get('UserSettings-PasswordMaxLength') + requirements.maxLength);
        }
        if (requirements.lower) {
          this.passwordRequirementsArray.push(this.res.get('UserSettings-PasswordLowercase'));
        }
        if (requirements.upper) {
          this.passwordRequirementsArray.push(this.res.get('UserSettings-PasswordUppercase'));
        }
        if (requirements.digit) {
          this.passwordRequirementsArray.push(this.res.get('UserSettings-PasswordNumber'));
        }
        if (requirements.symbol) {
          this.passwordRequirementsArray.push(this.res.get('UserSettings-PasswordSymbol'));
        }
      }
      this.passwordRequirementsArray.push(this.res.get('UserSettings-PasswordDifference'));
    }, 100);
  }

  public cancelPasswordChange() {
    window.location.reload();
  }

  public getAuthenticationCaption(): string | undefined {
    if (this.allowUserNameChange) {
      return this.res.get('UserSettings-Authentication');
    }
    return undefined;
  }

  public getTextInputClass(): string {
    return 'textInput';
  }

  public getSubmitButtonClass(): string {
    return 'submitButton';
  }

  public getCancelButtonClass(): string {
    return 'cancelButton';
  }
}
